import React, { useCallback, useMemo, useEffect } from 'react';
import { TypeOfRequestStep } from '../steps/TypeOfRequestStep';
import { NatureOfCrimeStep } from '../steps/NatureOfCrimeStep';
import { AgentInformationStep } from '../steps/AgentInformationStep';
import { TypeOfServiceStep } from '../steps/TypeOfServiceStep';
import { LegalOrderDetailsStep } from '../steps/LegalOrderDetailsStep';
import { FileUploadStep } from '../steps/FileUploadStep';
import { ReviewSubmitStep } from '../steps/ReviewSubmitStep';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';
import { Alert, AlertDescription } from '@/fluent-adapter';
import { useToast } from '@/fluent-adapter';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { WrenchScrewdriver24Regular as Construction } from '@fluentui/react-icons';
import type {
  TypeOfRequestForm,
  NatureOfCrimeForm,
  NDOForm,
  AgentInformationForm,
  TypeOfServiceForm,
  FileUploadForm,
  DomesticReviewForm,
  UploadedFile,
} from '../../store/types';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectDomesticWizard,
  selectIsSubmitting,
  selectSubmitSuccess,
  selectValidationErrors,
  updateTypeOfRequest,
  updateNatureOfCrime,
  updateNdo,
  updateAgentInfo,
  updateTypeOfService,
  addFile,
  removeFile,
  updateReview,
  setValidationErrors,
  submitRequestThunk,
  resetWizard,
} from '../../store/domesticWizardSlice';
import { validateDomesticStep, scrollToFirstError } from '../../utils/validation';

interface DomesticWizardProps {
  currentStep: number;
  onSubmitted?: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXL,
    width: '100%',
  },
  stepOuter: {
    // subtle background separation for steps that render multiple cards
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  placeholderCard: {
    maxWidth: '840px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  placeholderHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  spinnerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    rowGap: tokens.spacingVerticalL,
  },
  spinnerText: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
});

export const DomesticWizard: React.FC<DomesticWizardProps> = ({
  currentStep,
  onSubmitted,
}) => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const wizardState = useAppSelector(selectDomesticWizard);
  const isSubmitting = useAppSelector(selectIsSubmitting);
  const submitSuccess = useAppSelector(selectSubmitSuccess);
  const validationErrors = useAppSelector(selectValidationErrors);

  // Extract form data from Redux state
  const step1Data: TypeOfRequestForm = useMemo(() => ({
    cpConcern: wizardState.typeOfRequest.cpConcern,
    isPreservationRequest: wizardState.typeOfRequest.isPreservationRequest,
    title: wizardState.typeOfRequest.title,
    leReferenceNumber: wizardState.typeOfRequest.leReferenceNumber,
    isWAShieldLaw: wizardState.typeOfRequest.isWAShieldLaw,
    waShieldLawAcknowledgement: wizardState.typeOfRequest.waShieldLawAcknowledgement,
    hasDifferentRecipient: wizardState.typeOfRequest.hasDifferentRecipient,
    deliveryEmail: wizardState.typeOfRequest.deliveryEmail,
    notifyTarget: wizardState.typeOfRequest.notifyTarget,
    requestType: wizardState.typeOfRequest.requestType,
    requestSubType: wizardState.typeOfRequest.requestSubType,
    isDSAOrder: wizardState.typeOfRequest.isDSAOrder,
    legalOrderType: wizardState.typeOfRequest.legalOrderType,
  }), [
    wizardState.typeOfRequest.cpConcern,
    wizardState.typeOfRequest.isPreservationRequest,
    wizardState.typeOfRequest.title,
    wizardState.typeOfRequest.leReferenceNumber,
    wizardState.typeOfRequest.isWAShieldLaw,
    wizardState.typeOfRequest.waShieldLawAcknowledgement,
    wizardState.typeOfRequest.hasDifferentRecipient,
    wizardState.typeOfRequest.deliveryEmail,
    wizardState.typeOfRequest.notifyTarget,
    wizardState.typeOfRequest.requestType,
    wizardState.typeOfRequest.requestSubType,
    wizardState.typeOfRequest.isDSAOrder,
    wizardState.typeOfRequest.legalOrderType,
  ]);

  const step2Data: NatureOfCrimeForm = {
    isTerrorism: wizardState.natureOfCrime.isTerrorism,
    isChildExploitation: wizardState.natureOfCrime.isChildExploitation,
    isHumanTrafficking: wizardState.natureOfCrime.isHumanTrafficking,
    isMissingPerson: wizardState.natureOfCrime.isMissingPerson,
    isCorruption: wizardState.natureOfCrime.isCorruption,
    isCybercrime: wizardState.natureOfCrime.isCybercrime,
    isDefamation: wizardState.natureOfCrime.isDefamation,
    isHateSpeech: wizardState.natureOfCrime.isHateSpeech,
    isMoneyLaundering: wizardState.natureOfCrime.isMoneyLaundering,
    isHarassment: wizardState.natureOfCrime.isHarassment,
    isTheft: wizardState.natureOfCrime.isTheft,
    isViolentCrime: wizardState.natureOfCrime.isViolentCrime,
    isAdultSexualAbuseOrExploitation: wizardState.natureOfCrime.isAdultSexualAbuseOrExploitation,
    isDrugsOrDrugTrafficking: wizardState.natureOfCrime.isDrugsOrDrugTrafficking,
    isFraudWithMoneyLaundering: wizardState.natureOfCrime.isFraudWithMoneyLaundering,
    isFraudNoMoneyLaundering: wizardState.natureOfCrime.isFraudNoMoneyLaundering,
    isOther: wizardState.natureOfCrime.isOther,
    isOtherDescription: wizardState.natureOfCrime.isOtherDescription,
  };

  const step3Data: NDOForm = {
    hasNdo: wizardState.ndo.hasNdo,
    isNdoConfirm: wizardState.ndo.isNdoConfirm,
    listOfNumbersToExtend: wizardState.ndo.listOfNumbersToExtend,
    isNdoUploadFinalConfirm: wizardState.ndo.isNdoUploadFinalConfirm,
  };

  const step4Data: AgentInformationForm = useMemo(() => ({
    agencyName: wizardState.agentInfo.agencyName,
    agentFirstName: wizardState.agentInfo.agentFirstName,
    agentLastName: wizardState.agentInfo.agentLastName,
    agentEmail: wizardState.agentInfo.agentEmail,
    agentPhoneNumber: wizardState.agentInfo.agentPhoneNumber,
    agentPhoneCountryCode: wizardState.agentInfo.agentPhoneCountryCode,
    hasReceivingAgent: wizardState.agentInfo.hasReceivingAgent,
    receivingAgentFirstName: wizardState.agentInfo.receivingAgentFirstName,
    receivingAgentLastName: wizardState.agentInfo.receivingAgentLastName,
    receivingAgentEmail: wizardState.agentInfo.receivingAgentEmail,
    receivingAgentPhone: wizardState.agentInfo.receivingAgentPhone,
    receivingAgentPhoneCountryCode: wizardState.agentInfo.receivingAgentPhoneCountryCode,
    receivingAgentPhoneSameAsSubmitting: wizardState.agentInfo.receivingAgentPhoneSameAsSubmitting,
  }), [
    wizardState.agentInfo.agencyName,
    wizardState.agentInfo.agentFirstName,
    wizardState.agentInfo.agentLastName,
    wizardState.agentInfo.agentEmail,
    wizardState.agentInfo.agentPhoneNumber,
    wizardState.agentInfo.agentPhoneCountryCode,
    wizardState.agentInfo.hasReceivingAgent,
    wizardState.agentInfo.receivingAgentFirstName,
    wizardState.agentInfo.receivingAgentLastName,
    wizardState.agentInfo.receivingAgentEmail,
    wizardState.agentInfo.receivingAgentPhone,
    wizardState.agentInfo.receivingAgentPhoneCountryCode,
    wizardState.agentInfo.receivingAgentPhoneSameAsSubmitting,
  ]);

  const step5Data: TypeOfServiceForm = {
    selectedServiceIds: wizardState.typeOfService.selectedServiceIds,
    manualServices: wizardState.typeOfService.manualServices,
    identifiers: wizardState.typeOfService.identifiers,
    dataCategories: wizardState.typeOfService.dataCategories,
    otherDataCategories: wizardState.typeOfService.otherDataCategories,
    requestStartDate: wizardState.typeOfService.requestStartDate,
    requestEndDate: wizardState.typeOfService.requestEndDate,
  };

  // Project the tracked files into the shape the step component reads
  const step6Data: FileUploadForm = {
    files: wizardState.fileUpload.files.map((f): UploadedFile => ({
      id: f.id,
      name: f.name,
      size: f.size,
      type: f.type,
      uploadProgress: f.uploadProgress,
      uploaded: f.uploaded,
      file: f.file,
    })),
  };

  const step7Data: DomesticReviewForm = {
    acknowledgement: wizardState.review.acknowledgement,
    perjuryAcknowledgement: wizardState.review.perjuryAcknowledgement,
  };

  useEffect(() => {
    if (submitSuccess) {
      // Read before resetWizard() clears it: a part-attached submit is still a success,
      // but the files that did not make it have to be named while the case id is still
      // in hand. Resubmitting would file the case twice, so the message says so.
      const failed = wizardState.failedAttachments;
      const caseId = wizardState.submittedCaseId;

      toast(
        failed.length > 0
          ? {
              title: 'Request submitted, some files were not attached',
              description: `Case ${caseId ?? ''} was filed. These files did not attach and must be sent again on the case itself — do not resubmit the request: ${failed
                .map((attachment) => attachment.fileName)
                .join(', ')}.`,
              variant: 'destructive',
            }
          : {
              title: 'Request Submitted',
              description: 'Your law enforcement request has been submitted successfully.',
            },
      );
      onSubmitted?.();
      dispatch(resetWizard());
    }
  }, [
    submitSuccess,
    wizardState.failedAttachments,
    wizardState.submittedCaseId,
    onSubmitted,
    dispatch,
    toast,
  ]);

  useEffect(() => {
    if (wizardState.error) {
      toast({
        title: 'Submission Error',
        description: wizardState.error,
      });
    }
  }, [wizardState.error, toast]);

  // Form change handlers - dispatch to Redux
  const handleStep1Change = useCallback((data: Partial<TypeOfRequestForm>) => {
    dispatch(updateTypeOfRequest(data));
  }, [dispatch]);

  const handleStep2Change = useCallback((data: Partial<NatureOfCrimeForm>) => {
    dispatch(updateNatureOfCrime(data));
  }, [dispatch]);

  const handleStep3Change = useCallback((data: Partial<NDOForm>) => {
    dispatch(updateNdo(data));
  }, [dispatch]);

  const handleStep4Change = useCallback((data: Partial<AgentInformationForm>) => {
    dispatch(updateAgentInfo(data));
  }, [dispatch]);

  // Auto-set legalOrderType for domestic (USA) requests
  useEffect(() => {
    if (!wizardState.typeOfRequest.legalOrderType) {
      dispatch(updateTypeOfRequest({ legalOrderType: 'Microsoft Corporation' }));
    }
  }, [wizardState.typeOfRequest.legalOrderType, dispatch]);

  const handleStep5Change = useCallback((data: Partial<TypeOfServiceForm>) => {
    dispatch(updateTypeOfService(data));
  }, [dispatch]);

  const handleStep6Change = useCallback((_data: Partial<FileUploadForm>) => {
    // File changes are handled via specific file actions
  }, []);

  const handleStep7Change = useCallback((data: Partial<DomesticReviewForm>) => {
    dispatch(updateReview(data));
  }, [dispatch]);

  // File upload handlers using Redux and legacy service
  const onFileAdd = useCallback((file: File): string => {
    const id = globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    const newFile: UploadedFile = {
      id,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadProgress: 100,
      uploaded: true,
      file,
    };
    dispatch(addFile(newFile));
    return id;
  }, [dispatch]);

  const onFileRemove = useCallback((fileId: string) => {
    dispatch(removeFile(fileId));
  }, [dispatch]);

  // Handle final submission
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    // Validate every step through the shared validators, so submit enforces exactly
    // what each step's Next button does. A hand-rolled subset here drifted from
    // validation.ts and skipped whole steps (Type of Service, NDO, review).
    const totalSteps = 7;
    for (let step = 0; step < totalSteps; step++) {
      const result = validateDomesticStep(step, wizardState);
      if (!result.isValid) {
        dispatch(setValidationErrors(result.errors));
        scrollToFirstError(result.errors);
        return;
      }
    }

    try {
      // V3 files the case in a single call; the idempotency key and retry policy live
      // inside submitDomesticCase. Navigation happens via the submitSuccess effect.
      await dispatch(submitRequestThunk()).unwrap();
    } catch {
      // Error is surfaced through the slice's error state.
    }
  }, [isSubmitting, wizardState, dispatch]);

  // USA country constant for domestic requests
  const usaCountry = {
    id: 76,
    name: 'USA',
    code: 'USA',
    isDSACountry: false,
    manualDelivery: false,
    countryContactReview: false,
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className={styles.stepOuter}>
            <TypeOfRequestStep
              formData={step1Data}
              onFormChange={handleStep1Change}
              errors={validationErrors}
              isUSA={true}
            />
          </div>
        );

      case 1:
        return (
          <NatureOfCrimeStep
            formData={step2Data}
            onFormChange={handleStep2Change}
            cpConcern={wizardState.typeOfRequest.cpConcern}
            onCpConcernChange={(val) => handleStep1Change({ cpConcern: val })}
            errors={validationErrors}
          />
        );

      case 2:
        return (
          <AgentInformationStep
            formData={step4Data}
            onFormChange={handleStep4Change}
            typeOfRequestData={step1Data}
            onTypeOfRequestChange={handleStep1Change}
            officialEmail={undefined}
            errors={validationErrors}
          />
        );

      case 3:
        return (
          <TypeOfServiceStep
            formData={step5Data}
            onFormChange={handleStep5Change}
            errors={validationErrors}
            isPreservationRequest={step1Data.isPreservationRequest ?? false}
          />
        );

      case 4:
        return (
          <LegalOrderDetailsStep
            typeOfRequestData={step1Data}
            onTypeOfRequestChange={handleStep1Change}
            errors={validationErrors}
            isUSA={true}
            onDateOverride={(data) => dispatch(updateTypeOfService(data))}
            currentDates={{ requestStartDate: step5Data.requestStartDate, requestEndDate: step5Data.requestEndDate }}
          />
        );

      case 5:
        return (
          <FileUploadStep
            formData={step6Data}
            onFormChange={handleStep6Change}
            onFileAdd={onFileAdd}
            onFileRemove={onFileRemove}
            errors={validationErrors}
            country={usaCountry}
            ndoData={step3Data}
            onNdoChange={handleStep3Change}
            isChildExploitation={step1Data.cpConcern === true}
            isPreservationRequest={step1Data.isPreservationRequest === true}
          />
        );

      case 6:
        return (
          <ReviewSubmitStep
            formData={step7Data}
            allFormData={{
              step1: step1Data,
              step2: step2Data,
              step3: step3Data,
              step4: step4Data,
              step5: step5Data,
              step6: step6Data,
              step7: step7Data,
            }}
            country={usaCountry}
            onFormChange={handleStep7Change}
            onTypeOfRequestChange={handleStep1Change}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            errors={validationErrors}
            isUSA={true}
          />
        );

      default:
        return (
          <Card className={styles.placeholderCard}>
            <CardHeader>
              <CardTitle className={styles.placeholderHeader}>
                <Construction />
                Step Under Development
              </CardTitle>
              <CardDescription>This step is currently being developed</CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <Construction />
                <AlertDescription>Step {currentStep + 1} is not yet implemented.</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        );
    }
  };

  // Show full-page spinner during submission
  const showSpinner = isSubmitting;

  return (
    <>
      {showSpinner && (
        <div className={styles.spinnerOverlay}>
          <Spinner size="huge" />
          <span className={styles.spinnerText}>
            Submitting request...
          </span>
        </div>
      )}
      <div className={styles.root}>{renderCurrentStep()}</div>
    </>
  );
};
